import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui';
import { useOnboarding, useStepSaver, useStepValidity } from '../../../contexts/OnboardingContext';
import { useAppConfig } from '../../../contexts/AppConfigContext';
import { ProfileService } from '../../../api/services/ProfileService';
import { UserService } from '../../../api/services/UserService';
import type { Routemize_DTOs_Account_ProfileDto } from '../../../api/models/Routemize_DTOs_Account_ProfileDto';
import type { Routemize_DTOs_Account_UpdateProfileDto } from '../../../api/models/Routemize_DTOs_Account_UpdateProfileDto';
import { sanitizeFileForUpload } from '../../../utils/fileUtils';
import { ONBOARDING_STEP } from '../../../business/onboarding/OnboardingManager';
import { Callout, FieldLabel, StepIntro } from '../shared/StepShell';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const ProfileStep: React.FC = () => {
  const { completeStep } = useOnboarding();
  const { config, updateCurrentUser } = useAppConfig();
  const userId = config?.currentUser?.id;

  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<Routemize_DTOs_Account_ProfileDto | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fields, setFields] = useState({ name: '', surname: '', email: '', phoneNumber: '' });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [profileData, images] = await Promise.all([
        ProfileService.getApiAccountMyProfile(),
        userId ? UserService.getApiAppUserGetImage(userId).catch(() => []) : Promise.resolve([]),
      ]);
      if (cancelled) return;

      setProfile(profileData);
      setFields({
        name: profileData.name ?? '',
        surname: profileData.surname ?? '',
        email: profileData.email ?? '',
        phoneNumber: profileData.phoneNumber ?? '',
      });

      if (images?.[0]?.url) setPhotoUrl(images[0].url);
      setIsLoading(false);
    };

    load().catch(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useStepValidity(
    fields.name.trim().length > 0 &&
      fields.surname.trim().length > 0 &&
      fields.email.trim().length > 0 &&
      fields.phoneNumber.trim().length > 0
  );

  const handlePhotoUpload = async (file: File) => {
    if (!userId) return;
    setPhotoError(null);

    if (!file.type.startsWith('image/')) {
      setPhotoError('Please choose an image file.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setPhotoError('Images must be under 5MB.');
      return;
    }

    try {
      setIsUploading(true);
      await UserService.postApiAppUserUploadImage(userId, { file: sanitizeFileForUpload(file) });
      const images = await UserService.getApiAppUserGetImage(userId);
      const url = images?.[0]?.url;
      if (url) {
        // Cache-bust so the CDN copy doesn't shadow the new upload.
        setPhotoUrl(`${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`);
      }
    } catch {
      setPhotoError('That photo could not be uploaded. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  useStepSaver(
    ONBOARDING_STEP.PROFILE,
    useCallback(async () => {
      const update: Routemize_DTOs_Account_UpdateProfileDto = {
        name: fields.name,
        surname: fields.surname,
        email: fields.email,
        phoneNumber: fields.phoneNumber,
        userName: fields.email,
        concurrencyStamp: profile?.concurrencyStamp,
      };

      const updated = await ProfileService.putApiAccountMyProfile(update);
      setProfile(updated);

      // The app-configuration endpoint reads the name from auth
      // claims, which stay stale until the token is re-issued — refreshing here
      // would overwrite what was just saved. Update the cached user instead.
      updateCurrentUser({
        name: updated.name,
        surName: updated.surname,
        email: updated.email,
      });

      completeStep(ONBOARDING_STEP.PROFILE);
    }, [fields, profile, updateCurrentUser, completeStep])
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent-600" />
      </div>
    );
  }

  const initials =
    fields.name.charAt(0).toUpperCase() || fields.email.charAt(0).toUpperCase() || 'U';

  return (
    <div className="space-y-4">
      <StepIntro>
        Customers see your name and photo on their booking confirmation, and we use your number
        to reach you about appointments.
      </StepIntro>

      <div className="rounded-xl border border-ink-200 bg-surface p-4 sm:p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor="onboarding-first-name" required>
              First name
            </FieldLabel>
            <Input
              id="onboarding-first-name"
              value={fields.name}
              onChange={e => setFields(p => ({ ...p, name: e.target.value }))}
              autoComplete="given-name"
            />
          </div>
          <div>
            <FieldLabel htmlFor="onboarding-last-name" required>
              Last name
            </FieldLabel>
            <Input
              id="onboarding-last-name"
              value={fields.surname}
              onChange={e => setFields(p => ({ ...p, surname: e.target.value }))}
              autoComplete="family-name"
            />
          </div>
          <div className="sm:col-span-2">
            <FieldLabel htmlFor="onboarding-phone" required>
              Phone
            </FieldLabel>
            <Input
              id="onboarding-phone"
              type="tel"
              value={fields.phoneNumber}
              onChange={e => setFields(p => ({ ...p, phoneNumber: e.target.value }))}
              autoComplete="tel"
            />
          </div>
          <div className="sm:col-span-2">
            <FieldLabel htmlFor="onboarding-email" required>
              Email
            </FieldLabel>
            <Input
              id="onboarding-email"
              type="email"
              value={fields.email}
              onChange={e => setFields(p => ({ ...p, email: e.target.value }))}
              autoComplete="email"
            />
            <p className="mt-1 text-xs text-ink-400">This is also how you sign in.</p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3 border-t border-ink-100 pt-4">
          <div className="relative flex-shrink-0">
            <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl bg-accent-600">
              {photoUrl ? (
                <img src={photoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="text-lg font-bold text-white">{initials}</span>
              )}
            </div>
            {/* eslint-disable-next-line no-restricted-syntax -- avatar overlay trigger; sized to the image corner, no primitive fits */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              aria-label="Upload a profile photo"
              className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-lg bg-ink-900 text-ink-50 transition-colors hover:bg-ink-700 disabled:opacity-60"
            >
              {isUploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Camera className="h-3.5 w-3.5" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) handlePhotoUpload(file);
                e.target.value = '';
              }}
            />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink-800">Profile photo</p>
            <p className="text-xs text-ink-500">Optional — shown on booking confirmations.</p>
          </div>
        </div>

        {photoError && (
          <div className="mt-3">
            <Callout tone="danger">{photoError}</Callout>
          </div>
        )}
      </div>
    </div>
  );
};
